import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { TableLazyLoadEvent } from 'primeng/table';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Container } from '../../containers/container';
import { Group } from '../../shared/entities/group/group';
import { Role } from '../../shared/entities/role/role';
import { GroupsService } from '../../shared/services/groups/groups.service';
import { LoadingService } from '../../shared/services/loading.service';
import { LocationService } from '../../shared/services/location/location.service';
import { RolesService } from '../../shared/services/roles/roles.service';
import { ToastService } from '../../shared/services/toast/toast.service';
import { UsersService } from '../../shared/services/users/users.service';
import { AddRolesDialogComponent } from '../add-roles-dialog/add-roles-dialog.component';
import { ConfirmDialogEventType, ConfirmDialogSeverity } from '../confirmDialog/confirmDialogComponent';
import { ConfirmDialogService } from '../confirmDialog/confirmDialogService';
import { EditDialogService } from '../editDialog/editDialogService';
import { GroupDialogComponent } from '../group-dialog/group-dialog.component';
import * as fromGraphQl from './graphql';


@Component({
  selector: 'app-group-view',
  templateUrl: './group-view.component.html',
  styleUrls: ['./group-view.component.scss']
})
export class GroupViewComponent
  extends Container<fromGraphQl.GroupQueryResponse>
  implements OnInit, OnDestroy {

  dialogRef: DynamicDialogRef | undefined;

  public group: Group;
  public id: string;
  public routeSubscription: Subscription;
  public availableRoles: Role[];
  public groupRoles: Role[];
  public groupRolesFiltered: Role[];
  public selectedRoles: Role[];

  isRowSelectableFunction = this.isRowSelectable.bind(this);

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    loadingService: LoadingService,
    apollo: Apollo,
    public dialogService: EditDialogService,
    private confirmDialogService: ConfirmDialogService,
    @Inject('env') environment : any,
    private userService: UsersService,
    private toastService: ToastService,
    private locationService: LocationService,
    private rolesService: RolesService,
    private groupService: GroupsService,
  ) {
    super(apollo, loadingService, fromGraphQl.GROUP_QUERY, environment);
  }

  ngOnInit(): void {
    this.routeSubscription = this.route.params
      .pipe(
        filter((params) => params['id'] !== undefined),
        take(1)
      )
      .subscribe((params) => {
        this.id = params['id'];
        this.subscribeToQuery({
          id: this.id,
        });
      });
    this.rolesService.getRoles().pipe(take(1)).subscribe((roles) => {
      this.availableRoles = roles;
      this.filterGroupRoles();
    });
  }

  ngOnDestroy(): void {
    this.routeSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  handleSubscriptionResult(data: fromGraphQl.GroupQueryResponse): void {
    this.loadingService.setLoading(false);
    if (data) {
      this.queryRef.stopPolling();
      if (data.group) {
        this.group = new Group(data.group);
        this.availableRoles = Role.createRoles(data.roles);
        this.filterGroupRoles();
      }
    }
  }


  deleteGroup(): void {
    const message = 'Möchtest Du die Gruppe ' + this.group.name + ' wirklich löschen?';
    this.dialogRef = this.confirmDialogService.open({
      message: message,
      severity: ConfirmDialogSeverity.WARN,
      header: 'Gruppe löschen'
    });
    this.dialogRef.onClose.subscribe( (result : ConfirmDialogEventType) => {
      if(result === ConfirmDialogEventType.CONFIRM){
        this.loadingService.setLoading(true);
        this.groupService.deleteGroup(this.group, () => {
          this.loadingService.setLoading(false);
          this.toastService.addToastWithMessage('Gruppe gelöscht.');
          this.router.navigate(['/settings/groups']);
        });
      };
    })
  }

  filterGroupRoles(): void {
    if (this.group && this.availableRoles) {
      this.groupRoles = this.availableRoles.filter((role) => {
        return this.group.roles.find((groupRole) => groupRole === role.id);
      });
      this.groupRolesFiltered = this.groupRoles;
      console.log(this.groupRolesFiltered);
    }
  }

  removeRole(role: Role): void {
    const message = 'Möchtest Du die Rolle ' + role.label + ' wirklich entfernen?';
    this.dialogRef = this.confirmDialogService.open({
      message: message,
      severity: ConfirmDialogSeverity.WARN,
      header: 'Rolle entfernen'
    });
    this.dialogRef.onClose.subscribe( (result : ConfirmDialogEventType) => {
      if(result === ConfirmDialogEventType.CONFIRM){
        this.loadingService.setLoading(true);
        this.groupService.removeRoles(this.group, [role], () => {
          this.queryRef.refetch();
          this.toastService.addToastWithMessage('Rolle entfernt.');
        });
      };
    })
    // do nothing
  }

  removeRoles(): void {
    const message = 'Möchtest Du ' + this.selectedRoles?.length + ' Rolle(n) wirklich entfernen?';
    this.dialogRef = this.confirmDialogService.open({
      message: message,
      severity: ConfirmDialogSeverity.WARN,
      header: 'Rollen entfernen'
    });
    this.dialogRef.onClose.subscribe( (result : ConfirmDialogEventType) => {
      if(result === ConfirmDialogEventType.CONFIRM){
        this.loadingService.setLoading(true);
        this.groupService.removeRoles(this.group, this.selectedRoles, () => {
          this.queryRef.refetch();
          this.selectedRoles = undefined;
          this.toastService.addToastWithMessage('Rolle(n) entfernt.');
        });
      };
    })
    // do nothing
  }

  addRoles(): void {
    this.dialogRef = this.dialogService.open(AddRolesDialogComponent, {
      header: 'Rollen hinzufügen',
      data: {
        assignedRoles: this.groupRoles,
        availableRoles: this.availableRoles,
      }
    });
    this.dialogRef.onClose.subscribe((result) => {
      if(result){
        this.loadingService.setLoading(true);
        this.groupService.addRoles(this.group, result, () => {
          this.queryRef.refetch();
          this.toastService.addToastWithMessage('Rolle(n) hinzugefügt.');
        });
       };
    });
  }

  isRowSelectable(event: any): boolean {
    return !event.data.isInherited;
  }

  editGroup(): void {
    this.dialogRef = this.dialogService.open(GroupDialogComponent, {
      header: 'Gruppe bearbeiten',
      data: this.group
    });
    this.dialogRef.onClose.subscribe((result) => {
      // result is true or false, indiciating if it should refetch
      if(result){
        this.queryRef.refetch();
      }
    });
  }

  loadData(event: TableLazyLoadEvent) {
    //this.loading = true;

    // sorting

    //filtering
    let normalizedSearchTerm = (event.globalFilter as string)?.toLowerCase();

    // pagination
    if (normalizedSearchTerm) {
      this.groupRolesFiltered = this.groupRoles.filter((role) =>
        role.label?.toLowerCase().includes(normalizedSearchTerm)
      );
    }
    else {
      this.groupRolesFiltered = this.groupRoles;
    }


  }
}
